import { Capacitor } from '@capacitor/core';
import {
  LOG_LEVEL,
  Purchases,
  type CustomerInfo,
  type PurchasesOfferings,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import { RevenueCatUI, type PaywallResult } from '@revenuecat/purchases-capacitor-ui';
import type { ApiUser, BillingConfigResponse, BillingPlan } from '@shared/types';

let configuredFor: string | null = null;
let listenerConfigured = false;
const customerInfoSubscribers = new Set<(customerInfo: CustomerInfo) => void>();

export function isNativeBillingPlatform() {
  return Capacitor.isNativePlatform() && ['ios', 'android'].includes(Capacitor.getPlatform());
}

export function revenueCatApiKey(config?: BillingConfigResponse | null) {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return config?.revenueCat.iosApiKey ?? null;
  if (platform === 'android') return config?.revenueCat.androidApiKey ?? null;
  return null;
}

export async function configureRevenueCat(user: ApiUser, config: BillingConfigResponse) {
  if (!isNativeBillingPlatform()) return false;
  const apiKey = revenueCatApiKey(config);
  if (!apiKey) return false;
  const key = `${Capacitor.getPlatform()}:${apiKey}:${user.id}`;
  if (configuredFor === key) return true;

  await Purchases.setLogLevel({ level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.WARN }).catch(() => {});
  await Purchases.configure({ apiKey, appUserID: user.id });
  await Purchases.setEmail({ email: user.email }).catch(() => {});
  await Purchases.setDisplayName({ displayName: user.displayName }).catch(() => {});
  configuredFor = key;
  await ensureCustomerInfoListener();
  return true;
}

export async function getNativeOfferings(): Promise<PurchasesOfferings> {
  return Purchases.getOfferings();
}

// Plus and Pro packages live in SEPARATE RevenueCat offerings (lookup keys
// 'plus' and 'default'); only 'default' is the current offering, so plans must
// be resolved against offerings.all, not offerings.current.
export function packageForBillingPlan(
  offerings: PurchasesOfferings | null | undefined,
  plan: Pick<BillingPlan, 'tier' | 'revenueCatPackageType' | 'productId'>,
): PurchasesPackage | null {
  const offering = plan.tier === 'plus'
    ? offerings?.all?.plus
    : (offerings?.all?.default ?? offerings?.current);
  if (!offering) return null;
  const slot = plan.revenueCatPackageType === 'MONTHLY' ? offering.monthly : offering.annual;
  return slot ?? findPackage(offering.availablePackages, plan.revenueCatPackageType, plan.productId);
}

export async function purchaseNativePackage(pkg: PurchasesPackage): Promise<CustomerInfo> {
  const result = await Purchases.purchasePackage({ aPackage: pkg });
  return result.customerInfo;
}

export async function restoreNativePurchases(): Promise<CustomerInfo> {
  const result = await Purchases.restorePurchases();
  return result.customerInfo;
}

export async function getNativeCustomerInfo(): Promise<CustomerInfo> {
  const result = await Purchases.getCustomerInfo();
  return result.customerInfo;
}

export function hasActivePro(customerInfo: CustomerInfo | null | undefined, entitlementId = 'pro') {
  return Boolean(
    customerInfo?.entitlements.active[entitlementId]?.isActive ||
    customerInfo?.entitlements.active['squishy school Pro']?.isActive ||
    customerInfo?.entitlements.active.squishy_school_pro?.isActive,
  );
}

export async function presentNativePaywall(entitlementId = 'pro'): Promise<{ result: PaywallResult; customerInfo: CustomerInfo | null }> {
  const result = await RevenueCatUI.presentPaywallIfNeeded({
    requiredEntitlementIdentifier: entitlementId,
    displayCloseButton: true,
  });
  const customerInfo = await getNativeCustomerInfo().catch(() => null);
  return { result, customerInfo };
}

export async function presentNativeCustomerCenter() {
  await RevenueCatUI.presentCustomerCenter();
  return getNativeCustomerInfo().catch(() => null);
}

export function onNativeCustomerInfoUpdated(callback: (customerInfo: CustomerInfo) => void) {
  customerInfoSubscribers.add(callback);
  void ensureCustomerInfoListener();
  return () => {
    customerInfoSubscribers.delete(callback);
  };
}

async function ensureCustomerInfoListener() {
  if (listenerConfigured || !isNativeBillingPlatform()) return;
  listenerConfigured = true;
  await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
    for (const subscriber of customerInfoSubscribers) subscriber(customerInfo);
  }).catch(() => {
    listenerConfigured = false;
  });
}

function findPackage(
  packages: PurchasesPackage[],
  packageType: 'MONTHLY' | 'ANNUAL',
  productId: string,
) {
  // Store identifiers carry platform suffixes (iOS 'pro_monthly_v2',
  // Android 'pro_monthly:monthly'), so match on the shared prefix.
  return packages.find((pkg) => pkg.packageType === packageType || pkg.product.identifier.startsWith(productId)) ?? null;
}
